import { ApiError } from "@/api/fetchApi";
import { errorText } from "@/components/ErrorPanel";
import { type Action, type CompletedAction } from "@/generated/core/v1/actions";
import type { Date as PDate } from "@/generated/core/v1/date";
import { type BankConnection, BankConnection_Status } from "@/generated/backend/v1/bank_link";
import type { Family } from "@/generated/core/v1/family";
import type { FieldEdit, PlanRefreshAnswers } from "@/generated/core/v1/questionnaire";
import { ValueSource_Source } from "@/generated/core/v1/value_source";
import { formatDate, isSet, monthsBetween } from "@/plan-logic/lib/dates";
import { fieldEdit } from "@/plan-logic/questionnaire/build";
import { contributionRateValues } from "@/plan-logic/questionnaire/draft";

/**
 * The check-in (plan refresh): what the plan predicted the household did between the facts date
 * and today, confirmed or corrected, plus account balances when they need verifying. Built into
 * `PlanRefreshAnswers` for POST /api/family/check-in (engine ApplyPlanRefresh).
 */
export type Answer = { did?: boolean; amount?: number };

/** A copy of the action with its amount (or contribution rate) replaced by what the user actually did. */
export function withAmount(a: Action, amount: number): Action {
  const copy = structuredClone(a) as Action;
  const t = copy.actionType as { oneofKind?: string } & Record<string, unknown>;
  const inner = t.oneofKind ? (t[t.oneofKind] as Record<string, unknown>) : undefined;
  if (inner) {
    if ("amount" in inner) inner.amount = amount;
    else if ("downPayment" in inner) inner.downPayment = amount;
    else if ("employeeContributionRate" in inner) inner.employeeContributionRate = amount;
  }
  return copy;
}

/** Account balances, as field paths into the family. */
export type BalanceField = { path: string; uuid: string; label: string; current?: number };

const LISTS: [keyof Family["persons"][number], string, string][] = [
  ["chequingAccounts", "chequing_accounts", "Chequing"],
  ["tfsaAccounts", "tfsa_accounts", "TFSA"],
  ["rrspAccounts", "rrsp_accounts", "RRSP"],
  ["fhsaAccounts", "fhsa_accounts", "FHSA"],
  ["respAccounts", "resp_accounts", "RESP"],
  ["tradingAccounts", "trading_accounts", "Non-registered"],
  ["rrifAccounts", "rrif_accounts", "RRIF"],
];

export function balanceFields(family: Family): BalanceField[] {
  return family.persons.flatMap((p) =>
    LISTS.flatMap(([key, field, label]) =>
      (p[key] as { uuid: string; name: string; currentValue?: number }[]).map((a) => ({
        path: `persons[${p.uuid}].${field}[${a.uuid}].current_value`,
        uuid: a.uuid,
        label: a.name || `${p.givenName ? `${p.givenName}’s ` : ""}${label}`,
        current: a.currentValue,
      })),
    ),
  );
}

/**
 * What to call an entity a bank balance is waiting for: an account (as in "verify your balances"), a
 * credit card, a loan or a mortgage.
 */
export function entityLabel(family: Family, uuid: string): string {
  const account = balanceFields(family).find((b) => b.uuid === uuid);
  if (account) return account.label;
  for (const p of family.persons) {
    const card = p.creditCards.find((c) => c.uuid === uuid);
    if (card) return card.name || "Credit card";
  }
  const loan = family.loans.find((l) => l.uuid === uuid);
  if (loan) return loan.name || "Loan";
  const mortgage = family.mortgages.find((m) => m.uuid === uuid);
  if (mortgage) return mortgage.name || "Mortgage";
  return "Account";
}

const IMPORTED = new Set([
  ValueSource_Source.CSV,
  ValueSource_Source.FLINKS,
  ValueSource_Source.WEALTHICA,
]);

/** How old (in months) facts may get before the check-in asks to verify balances. */
export const STALE_MONTHS = 3;

/**
 * How old (in days) the household's facts must be before waiting bank balances alone open the
 * check-in. Sooner, they're applied whenever it opens for something else; bank balances change
 * daily, so counting them at once would prompt (and re-plan) every day.
 */
export const IMPORT_PROMPT_DAYS = 30;

export function daysBetween(a: PDate, b: PDate): number {
  return Math.round(
    (Date.UTC(b.year, b.month - 1, b.day) - Date.UTC(a.year, a.month - 1, a.day)) / 86_400_000,
  );
}

/**
 * Which accounts the user's bank connections fill: `fresh` while the connection is OK (an import
 * keeps them current), `stale` while it needs the user (their balances must be checked by hand).
 */
export type BankCoverage = { fresh: Set<string>; stale: Set<string> };

export const NO_BANKS: BankCoverage = { fresh: new Set(), stale: new Set() };

export function bankCoverage(connections: BankConnection[] | undefined): BankCoverage {
  const fresh = new Set<string>();
  const stale = new Set<string>();
  for (const c of connections ?? []) {
    const into = c.status === BankConnection_Status.NEEDS_RECONNECT ? stale : fresh;
    c.entityUuids.forEach((u) => into.add(u));
  }
  return { fresh, stale };
}

/**
 * Balances to verify (DEVIATIONS.md "Plan refresh"): those whose as-of date (their value source, else
 * the family's facts date) is more than STALE_MONTHS old, except imported ones a working bank
 * connection keeps current. An imported balance whose connection is gone is treated like any other.
 */
export function balancesToVerify(
  family: Family,
  today: PDate,
  banks: BankCoverage = NO_BANKS,
): BalanceField[] {
  return balanceFields(family).filter((b) => {
    const src = family.fieldSources[b.path];
    if (src && IMPORTED.has(src.source) && banks.fresh.has(b.uuid)) return false;
    const asOf = isSet(src?.asOf) ? src!.asOf : family.factsAsOf;
    if (!isSet(asOf)) return true;
    return monthsBetween(asOf, today) > STALE_MONTHS;
  });
}

export function buildAnswers(
  actions: Action[],
  answers: Record<number, Answer>,
  balances: Record<string, number | undefined>,
  today: PDate,
): PlanRefreshAnswers {
  const completed: CompletedAction[] = actions.map((a) => {
    const ans = answers[a.id];
    if (!ans?.did) return { abiRecommended: a };
    return {
      abiRecommended: a,
      userSubmitted: ans.amount !== undefined ? withAmount(a, ans.amount) : a,
    };
  });
  const verified: FieldEdit[] = Object.entries(balances)
    .filter(([, v]) => v !== undefined)
    .map(([path, v]) => fieldEdit(path, v!));
  // `imported` (bank balances) is added by the backend from the user's connections.
  return {
    asOf: { ...today },
    completedActions: completed,
    verifiedBalances: verified,
    imported: [],
  };
}

/**
 * What a failed save says. The engine's validation messages are for developers, so a 422 gets
 * plain words: an answer about an action the household can no longer match (its field errors are at
 * `answers.completed_actions[i]`), or else the amounts.
 */
export function checkInErrorText(e: unknown): string {
  const short = checkInShortfall(e);
  if (short) return doesntAddUpText(short);
  if (e instanceof ApiError && e.isConflict) {
    return "Your household changed somewhere else. Close this and open it again to see the latest.";
  }
  if (e instanceof ApiError && e.isValidation) {
    return e.fieldErrors.some((f) => f.path.startsWith("answers.completed_actions"))
      ? "We couldn't match one of the things you did to your household. Close this and open it again to see the latest, and let us know if it keeps happening."
      : "Some of these answers need another look. Check the amounts and try again.";
  }
  return errorText(e);
}

/** What the backend says about a check-in that runs the household's cash short. */
export type CheckInShortfall = { date?: PDate; amount?: number };

/**
 * A check-in whose answers spend more than the household had (422 `CHECK_IN_DOESNT_ADD_UP`, the
 * engine running short): when it ran out of cash, if the engine said. Null for any other error.
 */
export function checkInShortfall(e: unknown): CheckInShortfall | null {
  if (!(e instanceof ApiError) || e.code !== "CHECK_IN_DOESNT_ADD_UP") return null;
  const error = (e.body as { error?: Record<string, unknown> } | undefined)?.error ?? {};
  const date = error.shortfallDate as Partial<PDate> | undefined;
  const amount = error.shortfallAmount;
  return {
    date:
      date && typeof date.year === "number"
        ? { year: date.year, month: date.month ?? 0, day: date.day ?? 0 }
        : undefined,
    amount: typeof amount === "number" ? amount : undefined,
  };
}

/** The check-in's warning when its answers don't add up (Ian, 2026-10-28). */
export function doesntAddUpText(short: CheckInShortfall): string {
  const when = isSet(short.date) ? ` around ${formatDate(short.date)}` : "";
  return `These numbers don't add up with what we have for you: with them, your accounts would have run out of cash${when}. Some of your balances, income, or spending may have changed since. Update your data for a fuller picture.`;
}

/** Update Data, without its "Check in before you edit" gate (the check-in sends people there). */
export const UPDATE_DATA_SKIPPING_CHECK_IN = {
  to: "/app/update-data",
  state: { skipCheckIn: true },
} as const;

/** Whether a navigation asked Update Data to skip its check-in gate. */
export function skipsCheckIn(state: unknown): boolean {
  return (state as { skipCheckIn?: unknown } | null)?.skipCheckIn === true;
}

/** The answers so far, kept for the session (by family version and day) when the user leaves. */
export type CheckInDraft = {
  answers: Record<number, Answer>;
  balances: Record<string, number | undefined>;
  verify?: boolean;
};

const DRAFT_KEY = "abi.checkinDraft";
const draftId = (version: number, today: PDate) =>
  `${version}:${today.year}-${today.month}-${today.day}`;

export function saveDraft(version: number, today: PDate, draft: CheckInDraft): void {
  try {
    sessionStorage.setItem(DRAFT_KEY, JSON.stringify({ id: draftId(version, today), draft }));
  } catch {
    // ignore
  }
}

/** The kept answers, if they are for this family version and day. */
export function loadDraft(version: number, today: PDate): CheckInDraft | null {
  try {
    const stored = JSON.parse(sessionStorage.getItem(DRAFT_KEY) ?? "null");
    return stored?.id === draftId(version, today) ? (stored.draft as CheckInDraft) : null;
  } catch {
    return null;
  }
}

export function clearDraft(): void {
  try {
    sessionStorage.removeItem(DRAFT_KEY);
  } catch {
    // ignore
  }
}

const KEY = "abi.checkinDismissed";

export function dismissedToday(today: PDate): boolean {
  try {
    return localStorage.getItem(KEY) === `${today.year}-${today.month}-${today.day}`;
  } catch {
    return false;
  }
}

export function dismissForToday(today: PDate): void {
  try {
    localStorage.setItem(KEY, `${today.year}-${today.month}-${today.day}`);
  } catch {
    // ignore
  }
}

/** Contribution rates to pick from: Update Data's (0–15%) and the advised one (it can be 3.44%). */
export const rateOptions = (advised: number): [number, string][] =>
  contributionRateValues(advised).map((v) => [v, `${+(v * 100).toFixed(2)}%`]);
